import { useEffect, useRef, useState } from "react";
import {
  startMemberCatchupReview,
  getMemberCatchupPage,
  approveMemberCatchup,
  type MemberCatchupOverlap,
  type MemberCatchupPage,
  type MemberCatchupResult,
  type MemberCatchupReview,
} from "../../services/memberJourneyCatchup.service";

const MAX_SELECTED = 100;

const EXCLUSION_LABELS: Record<string, string> = {
  contact_unavailable: "Contact merged or removed",
  membership_inactive: "Membership not active",
  plan_start_date_missing: "No active plan start date",
  previous_enrollment: "Already in this journey",
  previously_evaluated: "Already checked by this journey",
  conditions_not_matched: "No longer matches this journey",
  previous_matching_email: "Already received this journey's email",
  email_missing: "No email address",
  email_opted_out: "Opted out of email",
  email_suppressed: "Email suppressed (bounce, spam report or unsubscribe)",
  subscription_group_missing: "Journey has no email category",
  group_unsubscribed: "Unsubscribed from this email category",
  membership_repair_protected: "Membership was restored, not a new signup",
  changed_since_preview: "Changed since the page was checked",
  enrollment_refused: "Journey refused the enrollment",
};

function exclusionLabel(code: string): string {
  return EXCLUSION_LABELS[code] ?? code.replaceAll("_", " ");
}

const when = (iso: string) => new Date(iso).toLocaleString();

/** Per-row warning: other member journeys this member is in or also qualifies for. */
function OverlapCell({ overlap }: { overlap?: MemberCatchupOverlap | null }) {
  if (!overlap) {
    return <span className="text-gray-500">None</span>;
  }
  return (
    <div className="space-y-1" data-testid="overlap-badge">
      {overlap.already_in.length > 0 && (
        <span className="block rounded bg-amber-100 px-2 py-0.5 text-amber-900">
          Already in: {overlap.already_in.join(", ")}
        </span>
      )}
      {overlap.also_qualifies.length > 0 && (
        <span className="block rounded bg-amber-100 px-2 py-0.5 text-amber-900">
          Also qualifies for: {overlap.also_qualifies.join(", ")}
        </span>
      )}
    </div>
  );
}

/**
 * "Review missed welcomes" for member-signup journeys that have it enabled.
 *
 * Paged (migration 2026-10-22-983): starting a review freezes the list of
 * members whose plan subscription started in the range and who were never
 * enrolled in or checked by this journey. Pages of up to 100 are checked one at
 * a time; each page is approved on its own (up to 100, re-checked at approval).
 * Selections never span pages. Nothing is enrolled or sent until "Enroll".
 */
export default function MemberJourneyCatchup({
  sequenceId,
  active,
  startPlans,
}: {
  sequenceId: string;
  active: boolean;
  startPlans: string[];
}) {
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [review, setReview] = useState<MemberCatchupReview | null>(null);
  const [page, setPage] = useState<MemberCatchupPage | null>(null);
  const [selected, setSelected] = useState<string[]>([]);
  const [reviewed, setReviewed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState<MemberCatchupResult[] | null>(null);
  // Every reset bumps the generation; a response from an older generation is ignored,
  // so a slow answer for an old range, journey or page can never be shown or approved.
  const generation = useRef(0);

  const clearPage = () => {
    setPage(null);
    setSelected([]);
    setReviewed(false);
    setResult(null);
  };
  const reset = () => {
    generation.current += 1;
    setReview(null);
    clearPage();
    setError("");
    setBusy(false);
  };
  useEffect(() => {
    // A different journey: nothing from the previous one may survive.
    reset();
    setFrom("");
    setTo("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sequenceId]);

  const message = (e: unknown) =>
    e instanceof Error
      ? e.message
      : String((e as { message?: string })?.message ?? e);

  async function openPage(reviewId: string, n: number, gen: number) {
    const p = await getMemberCatchupPage(reviewId, n);
    if (gen !== generation.current) {
      return;
    }
    clearPage();
    setPage(p);
  }

  async function startReview() {
    reset();
    const gen = generation.current;
    setBusy(true);
    try {
      const r = await startMemberCatchupReview(sequenceId, from, to);
      if (gen !== generation.current) {
        return;
      }
      setReview(r);
      if (r.page_count > 0) {
        await openPage(r.review_id, 1, gen);
      }
    } catch (e) {
      if (gen === generation.current) {
        setError(message(e));
      }
    } finally {
      if (gen === generation.current) {
        setBusy(false);
      }
    }
  }

  async function goTo(n: number) {
    if (!review) {
      return;
    }
    generation.current += 1;
    const gen = generation.current;
    clearPage();
    setError("");
    setBusy(true);
    try {
      await openPage(review.review_id, n, gen);
    } catch (e) {
      if (gen === generation.current) {
        setError(message(e));
      }
    } finally {
      if (gen === generation.current) {
        setBusy(false);
      }
    }
  }

  async function approve() {
    if (!page || !reviewed || selected.length === 0) {
      return;
    }
    const gen = generation.current;
    setBusy(true);
    setError("");
    try {
      const r = await approveMemberCatchup(page.batch_id, selected);
      if (gen === generation.current) {
        setResult(r);
        setSelected([]);
        setReviewed(false);
      }
    } catch (e) {
      if (gen === generation.current) {
        setError(message(e));
      }
    } finally {
      if (gen === generation.current) {
        setBusy(false);
      }
    }
  }

  const planList = startPlans.join(", ");
  const opened = page?.opened_pages.length ?? 0;
  const allOpened = !!page && opened >= page.page_count;
  const expiredError = /expired/i.test(error);
  return (
    <section
      className="space-y-4 rounded-lg border bg-white p-5 mb-6"
      aria-labelledby="member-catchup-heading"
    >
      <h2 id="member-catchup-heading" className="text-lg font-semibold">
        Review missed welcomes
      </h2>
      <p>
        Find members whose plan subscription started in the dates you choose but
        who never entered this journey. The start date is the earliest active
        subscription for one of these plans: {planList}. It comes from the
        member&apos;s subscription records and can differ from the date they
        first joined. Members already in this journey, or already checked by it,
        are not listed. Reviewing does not enroll anyone or send email.
        Approving selected members starts this journey for them and may send its
        first email right away.
      </p>
      <div className="flex flex-wrap gap-4 items-end">
        <label>
          Plan subscription started from
          <input
            aria-label="Plan subscription started from"
            className="block border rounded p-2"
            type="date"
            value={from}
            disabled={busy}
            onChange={(e) => {
              reset();
              setFrom(e.target.value);
            }}
          />
        </label>
        <label>
          Plan subscription started through
          <input
            aria-label="Plan subscription started through"
            className="block border rounded p-2"
            type="date"
            value={to}
            disabled={busy}
            onChange={(e) => {
              reset();
              setTo(e.target.value);
            }}
          />
        </label>
        <button
          type="button"
          className="border rounded px-4 py-2 disabled:opacity-50"
          disabled={busy || !from || !to || from > to}
          onClick={startReview}
        >
          {busy && !review ? "Working…" : "Start review"}
        </button>
      </div>
      <p className="text-sm text-gray-600">
        Up to 93 days at a time, ending today or earlier. Members are reviewed
        100 per page.
      </p>
      {error && (
        <div role="alert" className="text-red-700">
          <p>{error}</p>
          {expiredError && (
            <button
              type="button"
              className="mt-2 border rounded px-3 py-1"
              onClick={startReview}
              disabled={busy || !from || !to}
            >
              Start a new review
            </button>
          )}
        </div>
      )}

      {review && (
        <div data-testid="review-summary" className="rounded border p-3">
          <p>
            Reviewing members whose plan subscription started{" "}
            <strong>
              {review.date_from} through {review.date_to}
            </strong>
            .
          </p>
          {review.member_count === 0 ? (
            <p>
              No members to review in this range. {review.already_handled}{" "}
              already in or checked by this journey were left out.
            </p>
          ) : (
            <p>
              {review.member_count} preliminary members in {review.page_count}{" "}
              {review.page_count === 1 ? "page" : "pages"} (plan started in the
              range and not yet in or checked by this journey). Each page is
              checked when you open it, so the number who can finally be
              enrolled is only known page by page. {review.already_handled}{" "}
              already in or checked by this journey were left out. Members whose
              plan starts after you started this review are not added; start a
              new review to include them. This review expires{" "}
              {when(review.expires_at)}.
            </p>
          )}
        </div>
      )}

      {review && !page && busy && (
        <p role="status" aria-live="polite">
          Checking page…
        </p>
      )}
      {review && page && (
        <>
          <nav
            aria-label="Review pages"
            className="flex flex-wrap items-center gap-3"
          >
            <button
              type="button"
              className="border rounded px-3 py-1 disabled:opacity-50"
              disabled={busy || page.page <= 1}
              onClick={() => goTo(page.page - 1)}
            >
              Previous page
            </button>
            <span data-testid="page-position">
              Page {page.page} of {page.page_count}
            </span>
            <button
              type="button"
              className="border rounded px-3 py-1 disabled:opacity-50"
              disabled={busy || page.page >= page.page_count}
              onClick={() => goTo(page.page + 1)}
            >
              Next page
            </button>
            <button
              type="button"
              className="border rounded px-3 py-1 disabled:opacity-50"
              disabled={busy}
              onClick={() => goTo(page.page)}
            >
              Re-check this page
            </button>
          </nav>
          <p data-testid="page-summary">
            This page: {page.eligible} eligible, {page.excluded} excluded
            {page.not_in_journey > 0 &&
              `, ${page.not_in_journey} not matching this journey`}
            {page.not_new_start > 0 &&
              `, ${page.not_new_start} not a new start in this range`}
            {page.contact_removed > 0 &&
              `, ${page.contact_removed} contact removed`}{" "}
            (of {page.evaluated} members on this page).
          </p>
          <p data-testid="review-progress" className="text-sm">
            Pages opened: {opened} of {page.page_count}. Enrolled through this
            review so far: {page.enrolled_in_review}.{" "}
            {allOpened
              ? "Every page has been opened."
              : `${page.page_count - opened} ${
                  page.page_count - opened === 1 ? "page" : "pages"
                } not opened yet — the review is not finished.`}
          </p>
          {page.eligible === 0 && !result && (
            <p role="note" data-testid="no-eligible-note">
              No one on this page can be enrolled.
              {page.page < page.page_count
                ? " Continue to the next page; more members are waiting to be reviewed."
                : ""}
            </p>
          )}
          <p className="text-sm text-gray-600">
            Select up to {MAX_SELECTED} eligible members on this page.
            Selections apply to this page only: moving to another page,
            re-checking or approving clears them. To approve the rest of a page
            after an approval, use &quot;Re-check this page&quot;. This
            page&apos;s check expires {when(page.expires_at)}.
          </p>
          {page.overlap_count > 0 && (
            <div
              role="note"
              data-testid="overlap-banner"
              className="rounded border border-amber-300 bg-amber-50 p-3 text-amber-900"
            >
              <strong>
                {page.overlap_count} of the members on this page also qualify
                for or are already in another member journey.
              </strong>{" "}
              Approving them here sends this journey&apos;s welcome in addition
              to the other journey&apos;s. Check the &quot;Other member
              journeys&quot; column and pick the one journey that fits each
              member. Nobody is selected for you.
            </div>
          )}
          {page.candidates.length > 0 && (
            <div className="overflow-x-auto max-h-96">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left">
                    <th>Select</th>
                    <th>Member</th>
                    <th>Email</th>
                    <th>Plan subscription started</th>
                    <th>Prior email from this journey</th>
                    <th>Other member journeys</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {page.candidates.map((c) => (
                    <tr key={c.contact_id} className="border-t">
                      <td>
                        <input
                          type="checkbox"
                          aria-label={`Select ${c.name || c.email || c.contact_id}`}
                          checked={selected.includes(c.contact_id)}
                          disabled={
                            busy ||
                            !!result ||
                            !!c.exclusion ||
                            (!selected.includes(c.contact_id) &&
                              selected.length >= MAX_SELECTED)
                          }
                          onChange={(e) => {
                            setReviewed(false);
                            setSelected((old) =>
                              e.target.checked
                                ? [...old, c.contact_id]
                                : old.filter((x) => x !== c.contact_id),
                            );
                          }}
                        />
                      </td>
                      <td>{c.name || "—"}</td>
                      <td>{c.email || "Missing"}</td>
                      <td>{c.started_on ?? "—"}</td>
                      <td>
                        {c.prior_matching_email_at
                          ? when(c.prior_matching_email_at)
                          : "None recorded"}
                      </td>
                      <td>
                        <OverlapCell overlap={c.overlap} />
                      </td>
                      <td>
                        {c.exclusion
                          ? `Excluded: ${exclusionLabel(c.exclusion)}`
                          : "Eligible when checked"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {!result && (
            <>
              <label className="block">
                <input
                  type="checkbox"
                  checked={reviewed}
                  disabled={busy || selected.length === 0}
                  onChange={(e) => setReviewed(e.target.checked)}
                />{" "}
                I reviewed these {selected.length} members and approve enrolling
                them.
              </label>
              {!active && (
                <p>
                  The journey must be active before missed welcomes can be
                  approved. Start a new review after activating it.
                </p>
              )}
              <button
                type="button"
                className="bg-blue-700 text-white rounded px-4 py-2 disabled:opacity-50"
                disabled={busy || !active || !reviewed || selected.length === 0}
                onClick={approve}
              >
                Enroll {selected.length} selected members
              </button>
            </>
          )}
        </>
      )}
      {result && (
        <div role="status">
          <p>
            {result.filter((r) => r.outcome === "enrolled").length} enrolled;{" "}
            {result.filter((r) => r.outcome !== "enrolled").length} skipped
            after rechecking. This page&apos;s approval is used up: use
            &quot;Re-check this page&quot; to approve more members from it, or
            go to the next page.
          </p>
          <ul>
            {result
              .filter((r) => r.outcome !== "enrolled")
              .map((r) => (
                <li key={r.contact_id}>
                  {page?.candidates.find((c) => c.contact_id === r.contact_id)
                    ?.name || r.contact_id}
                  : {exclusionLabel(r.outcome)}
                </li>
              ))}
          </ul>
        </div>
      )}
    </section>
  );
}
